import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "flowbite-react";
import { RefreshCw, Loader2 } from "lucide-react";
import { InlineErrorBanner } from "@/features/employeeTransfer/components/InlineErrorBanner";
import { useOtpResendCooldown } from "@/features/employeeTransfer/hooks/useOtpResendCooldown";
import { type OtpFormData, otpFormSchema } from "@/features/employeeTransfer/schemas/otpForm.schema";
import { ControlledOTPInput } from "@/shared/components/forms/ControlledOTPInput";
import { P } from "@/shared/ui/typographies/paragraph";

type OtpStepProps = {
  phoneHint: string
  isMutating: boolean
  otpError: string | null
  onVerify: (code: string) => void
  onResend: () => void
  description?: string
};

export const OtpStep = ({
  phoneHint,
  isMutating,
  otpError,
  onVerify,
  onResend,
  description,
}: OtpStepProps) => {
  const { remainingSeconds, isOnCooldown, startCooldown } = useOtpResendCooldown();

  const {
    control, handleSubmit, watch, reset,
  } = useForm<OtpFormData>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { otp_code: "" },
    mode: "onSubmit",
  });

  const otpCode = watch("otp_code");

  const onSubmitForm = useCallback((formData: OtpFormData) => {
    onVerify(formData.otp_code);
  }, [onVerify]);

  useEffect(() => {
    if (otpCode.length === 6 && !isMutating) {
      handleSubmit(onSubmitForm)();
    }
  }, [otpCode, isMutating, handleSubmit, onSubmitForm]);

  useEffect(() => {
    if (otpError) {
      reset({ otp_code: "" });
    }
  }, [otpError, reset]);

  const handleResend = () => {
    onResend();
    startCooldown();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <P size="sm" color="muted" className="text-pretty tracking-wider">
          {description ?? "Pour sécuriser le transfert, un code de vérification a été envoyé par SMS au numéro"}
          {" "}
          <strong className="text-gray-900 dark:text-white">{phoneHint}</strong>
          .
        </P>
        <P size="sm" color="muted" className="text-pretty tracking-wider">
          Saisissez le code à 6 chiffres ci-dessous.
        </P>
      </div>

      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        <ControlledOTPInput
          id="otp-input"
          label="Code de vérification"
          name="otp_code"
          control={control}
          autoFocus
          isRequired
        />

        {otpError && <InlineErrorBanner message={otpError} />}

        <Button
          type="submit"
          color="primary"
          fullSized
          disabled={isMutating || otpCode.length < 6}
        >
          {isMutating
            ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Vérification en cours...
              </>
            )
            : "Vérifier le code"}
        </Button>
      </form>

      <div className="flex items-center justify-center">
        <Button
          color="light"
          size="xs"
          onClick={handleResend}
          disabled={isOnCooldown || isMutating}
          data-testid="otp-resend"
        >
          <RefreshCw size={14} className="mr-1.5" />
          {isOnCooldown
            ? `Renvoyer le code (${remainingSeconds}s)`
            : "Renvoyer le code"}
        </Button>
      </div>
    </div>
  );
};
